import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import { Dialog, Button, TextInput, Image } from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
  RequiredMessage,
} from "@utils/extra-style";
import {
  HeaderWrapper,
  Title,
  ButtonWrapper,
  FormBody,
  OptionsWrapper,
  FlexRow,
  SectionContainer,
  GridRow,
  ContainerLabel,
  CountrySubMessage,
  BorderImage,
  DashboardList,
  DashboardCard,
  DashboardCheckbox,
  DashboardContent,
  DashboardHeader,
  DashboardTitle,
  DashboardStatus,
  DashboardMeta,
  DashboardMetaSeparator,
  DashboardDescription,
  DashboardEmpty,
} from "./AddEditAdmin.styled";
import closeIcon from "@icons/ic-close-white.svg";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import {
  countryflagIcons as flagIcons,
  defaultCountryFlag,
} from "@utils/country-data";

const AddEditAdmin = (props) => {
  const {
    t,
    id,
    isEditMode,
    title,
    onClose,
    updateRefeshStatus,
    selectedAdmin,
  } = props;
  const [isAdminCreating, setIsAdminCreating] = useState(false);
  const [createAdminAPIError, setCreateAdminAPIError] = useState("");
  const generalParams = useGetGeneralParameters();
  const { countryList } = generalParams;

  // Superset reporting dashboards grant feature
  const [dashboardList, setDashboardList] = useState([]);
  const [checkedDashboardUuids, setCheckedDashboardUuids] = useState(
    isEditMode ? selectedAdmin?.reportingDashboards ?? [] : []
  );

  useEffect(() => {
    axios
      .get("/general/reportingDashboards")
      .then(({ data }) => setDashboardList(Array.isArray(data) ? data : []))
      .catch(() => setDashboardList([]));
  }, []);

  const onDashboardToggle = (dashboardUuid) => {
    setCheckedDashboardUuids((uuids) =>
      uuids.includes(dashboardUuid)
        ? uuids.filter((uuid) => uuid !== dashboardUuid)
        : [...uuids, dashboardUuid]
    );
  };
  // The popup body is capped in height : the dashboards list takes the room
  // left below the fields above it and scrolls inside that space, instead of
  // spilling over the buttons. The space is handed to the CSS as a variable.
  const formBodyRef = useRef(null);
  const dashboardListRef = useRef(null);

  useLayoutEffect(() => {
    const updateDashboardListSpace = () => {
      const formBody = formBodyRef.current;
      const list = dashboardListRef.current;
      if (!formBody || !list) return;
      const bodyMaxHeight = parseFloat(getComputedStyle(formBody).maxHeight);
      if (Number.isNaN(bodyMaxHeight)) return;
      const bottomGap = 32;
      const space =
        formBody.getBoundingClientRect().top +
        bodyMaxHeight -
        list.getBoundingClientRect().top -
        bottomGap;
      list.style.setProperty("--dashboard-list-space", `${space}px`);
    };
    updateDashboardListSpace();
    window.addEventListener("resize", updateDashboardListSpace);
    return () =>
      window.removeEventListener("resize", updateDashboardListSpace);
  }, [dashboardList, countryList]);

  const sortedCountryList = [...countryList]
    .sort((a, b) => a.label.localeCompare(b.label))
    .filter((country) => country.platform === 1);

  const getCountryArray = (countries) => {
    const countryIds = [];
    const countryData = countries.find((data) => !!data.main);
    countryIds.push(countryData.country.id);
    countries.map((data) => {
      if (countryIds.indexOf(data.country.id) === -1) {
        countryIds.push(data.country.id);
      }
    });
    return countryIds;
  };

  const [formData, setFormData] = useState({
    firstName: {
      required: true,
      value: selectedAdmin?.firstName ?? "",
      error: "",
    },
    lastName: {
      required: true,
      value: selectedAdmin?.lastName ?? "",
      error: "",
    },
    phone: {
      required: true,
      value: selectedAdmin?.phone ?? "",
      error: "",
    },
    email: {
      required: true,
      value: selectedAdmin?.email ?? "",
      error: "",
    },
    job: {
      required: true,
      value: selectedAdmin?.job ?? "",
      error: "",
    },
    countries: {
      required: true,
      value: isEditMode ? getCountryArray(selectedAdmin?.countries) : [],
      error: "",
    },
    isTech: {
      required: true,
      value: selectedAdmin?.isTech,
      error: "",
    },
  });

  const onFirstNameChange = (e) => {
    setFormData({
      ...formData,
      firstName: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onLastNameChange = (e) => {
    setFormData({
      ...formData,
      lastName: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onEmailChange = (e) => {
    setFormData({
      ...formData,
      email: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onPhoneChange = (e) => {
    setFormData({
      ...formData,
      phone: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onJobChange = (e) => {
    setFormData({
      ...formData,
      job: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.firstName.value) {
      isFormValid = false;
      data.firstName.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.lastName.value) {
      isFormValid = false;
      data.lastName.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.email.value) {
      isFormValid = false;
      data.email.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (
      data.email.value &&
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(data.email.value)
    ) {
      isFormValid = false;
      data.email.error = "INVALID_EMAIL_FORMAT";
    }
    if (data.phone.value && !/^[0-9+]{8,14}$/i.test(data.phone.value)) {
      isFormValid = false;
      data.phone.error = t("INVALID_PHONE_FORMAT");
    }
    if (data.countries.value?.length === 0) {
      isFormValid = false;
      data.countries.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!isFormValid) {
      setFormData({ ...data });
    }

    return isFormValid;
  };

  async function checkEmailExist(email) {
    const response = await axios.get(
      `/general/search?email=${email}&limit=5&objectTypes=["adminEmail"]`
    );
    return response.data;
  }

  const getFormData = () => {
    const countryData = [];
    if (formData.countries.value?.length > 0) {
      if (!isEditMode) {
        formData.countries.value.map((countryInfo, index) => {
          const mainStaus = index === 0 ? 1 : 0;
          countryData.push({
            id: null,
            country: { id: countryInfo },
            main: mainStaus,
          });
        });
      }
    }
    if (isEditMode) {
      const existingCountries = selectedAdmin?.countries;
      formData.countries.value.map((countryInfo, index) => {
        const existingCountry = existingCountries.find(
          (data) => data.country.id === countryInfo
        );
        const mainStaus = index === 0 ? 1 : 0;
        countryData.push({
          id: existingCountry?.id ?? null,
          country: { id: countryInfo },
          main: mainStaus,
        });
      });
    }
    const data = {
      id: isEditMode ? selectedAdmin.id : "",
      firstname: formData.firstName.value,
      lastname: formData.lastName.value,
      email: formData.email.value,
      phone: formData.phone.value,
      isTech: formData.isTech.value ? 1 : "0",
      job: formData.job.value,
      isActive: isEditMode ? selectedAdmin.isActive : 1,
      countries: JSON.stringify(countryData),
      reportingDashboards: JSON.stringify(checkedDashboardUuids),
    };
    return data;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      checkEmailExist(formData.email.value).then((response) => {
        if (
          response?.admins.length === 0 ||
          (isEditMode &&
            response?.admins.length === 1 &&
            response?.admins[0].id === id)
        ) {
          setIsAdminCreating(true);
          setCreateAdminAPIError("");
          // Create solution
          if (!id && !isEditMode) {
            axios
              .post("/general/admins", getFormData())
              .then(() => {
                setIsAdminCreating(false);
                updateRefeshStatus();
                onClose();
              })
              .catch((error) => {
                setIsAdminCreating(false);
                setCreateAdminAPIError(error.response?.data?.message ?? "");
              });
          }
          if (id && isEditMode) {
            axios
              .put(`/general/admins/${id}`, getFormData())
              .then(() => {
                setIsAdminCreating(false);
                updateRefeshStatus();
                onClose();
              })
              .catch((error) => {
                setIsAdminCreating(false);
                setCreateAdminAPIError(error.response?.data?.message ?? "");
              });
          }
        } else {
          formData.email.error = "ADMIN_ERROR_EMAIL_USED";
          setFormData({ ...formData });
        }
      });
    }
  };

  const getBorderColor = (countryId) => {
    let color = "solid 0px #fff";
    const countryIndex = formData.countries.value.indexOf(countryId);
    if (countryIndex === 0) {
      color = "solid 2px #32A728";
    }
    if (countryIndex > 0) {
      color = "solid 2px #9CD9C1";
    }
    return color;
  };

  const onCountryUpdate = (countryId) => {
    const countryIds = formData.countries.value;
    const countryIndex = formData.countries.value.indexOf(countryId);
    if (countryIndex === -1) {
      countryIds.push(countryId);
    }
    if (countryIndex >= 0) {
      countryIds.splice(countryIndex, 1);
    }
    setFormData({
      ...formData,
      countries: {
        value: countryIds,
        error: "",
        required: true,
      },
    });
  };

  return (
    <Dialog>
      <div>
        <HeaderWrapper>
          <Title>{title}</Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt={t("CLOSE")}
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        <FormBody ref={formBodyRef}>
          <OptionsWrapper>
            <FlexRow>
              <SectionContainer>
                <div>
                  <GridRow column="1fr 1fr">
                    <TextInput
                      id="admin_firstName"
                      type="text"
                      label={t("ADMIN_FIRSTNAME")}
                      isFloat
                      required
                      value={String(formData.firstName.value)}
                      onChange={(e) => onFirstNameChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.firstName.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.firstName.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.firstName.error}
                      placeholder={t("ADMIN_FIRSTNAME")}
                    />
                    <TextInput
                      id="admin_lastName"
                      type="text"
                      label={t("ADMIN_LASTNAME")}
                      isFloat
                      required
                      value={String(formData.lastName.value)}
                      onChange={(e) => onLastNameChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.lastName.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.lastName.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.lastName.error}
                      placeholder={t("ADMIN_LASTNAME")}
                    />
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <TextInput
                      id="admin_email"
                      type="text"
                      label={t("ADMIN_EMAIL")}
                      isFloat
                      required
                      value={String(formData.email.value)}
                      onChange={(e) => onEmailChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.email.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.email.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.email.error}
                      placeholder={t("ADMIN_EMAIL")}
                    />
                    <TextInput
                      id="bank_phone"
                      type="text"
                      label={t("ADMIN_PHONE")}
                      isFloat
                      value={String(formData.phone.value)}
                      onChange={(e) => onPhoneChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.phone.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.phone.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.phone.error}
                      placeholder={t("ADMIN_PHONE")}
                    />
                  </GridRow>

                  <GridRow column="1fr 1fr">
                    <TextInput
                      id="bank_iban"
                      type="text"
                      label={t("ADMIN_JOB_TITLE")}
                      isFloat
                      value={String(formData.job.value)}
                      onChange={(e) => onJobChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.job.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.job.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.job.error}
                      placeholder={t("ADMIN_JOB_TITLE")}
                    />
                    <div style={{ marginTop: "1.5rem" }}>
                      <Image
                        src={formData.isTech.value ? enableIcon : disableIcon}
                        alt={t("ADMIN_CHOICE_IS_TECH")}
                        title={t("ADMIN_CHOICE_IS_TECH")}
                        styles={{ width: "auto" }}
                        onClick={() => {
                          setFormData({
                            ...formData,
                            isTech: {
                              value: !formData.isTech.value,
                              error: "",
                              required: true,
                            },
                          });
                        }}
                      />
                      <label className="label admins-list__active-label">
                        {t("ADMIN_CHOICE_IS_TECH")}
                      </label>
                    </div>
                  </GridRow>
                  <GridRow column="1fr" gap="0rem">
                    <ContainerLabel>{t("COUNTRIES")}</ContainerLabel>
                    <CountrySubMessage>
                      {t("COUNTRIES_ADMIN_EXPLANATION")}
                    </CountrySubMessage>
                  </GridRow>
                  <GridRow column="1fr">
                    <FlexRow>
                      {sortedCountryList?.map((info, infoIndex) => {
                        return (
                          <BorderImage
                            border={getBorderColor(info.id)}
                            key={
                              "add_edit_admin_country" +
                              infoIndex +
                              "=" +
                              info.id
                            }
                          >
                            <Image
                              src={
                                flagIcons[info.id]
                                  ? flagIcons[info.id]
                                  : defaultCountryFlag
                              }
                              alt="Avatar"
                              styles={{ width: "40px" }}
                              onClick={() => onCountryUpdate(info.id)}
                            />
                          </BorderImage>
                        );
                      })}
                    </FlexRow>
                  </GridRow>
                  {formData.countries.error && (
                    <GridRow>
                      <RequiredMessage>
                        {formData.countries.error}
                      </RequiredMessage>
                    </GridRow>
                  )}
                  <GridRow column="1fr" gap="0rem">
                    <ContainerLabel>
                      {t("ADMIN_REPORTING_DASHBOARDS")}
                    </ContainerLabel>
                    <CountrySubMessage>
                      {t("ADMIN_REPORTING_DASHBOARDS_EXPLANATION")}
                    </CountrySubMessage>
                  </GridRow>
                  <GridRow column="1fr">
                    <DashboardList ref={dashboardListRef}>
                      {dashboardList.map((dashboard) => (
                        <DashboardCard
                          key={dashboard.uuid ?? dashboard.id}
                          $disabled={!dashboard.uuid}
                        >
                          <DashboardCheckbox
                            type="checkbox"
                            disabled={!dashboard.uuid}
                            checked={checkedDashboardUuids.includes(dashboard.uuid)}
                            onChange={() => onDashboardToggle(dashboard.uuid)}
                          />
                          <DashboardContent>
                            <DashboardHeader>
                              <DashboardTitle>{dashboard.title}</DashboardTitle>
                              <DashboardStatus
                                published={dashboard.status === "PUBLISHED"}
                              >
                                {t(dashboard.status)}
                              </DashboardStatus>
                            </DashboardHeader>
                            <DashboardMeta>
                              <strong>ID</strong> {dashboard.id}
                              <DashboardMetaSeparator>·</DashboardMetaSeparator>
                              <strong>UUID</strong> {dashboard.uuid}
                            </DashboardMeta>
                            {dashboard.description && (
                              <DashboardDescription>
                                {dashboard.description}
                              </DashboardDescription>
                            )}
                          </DashboardContent>
                        </DashboardCard>
                      ))}
                      {dashboardList.length === 0 && (
                        <DashboardEmpty>
                          {t("REPORTS_NONE_ASSIGNED")}
                        </DashboardEmpty>
                      )}
                    </DashboardList>
                  </GridRow>
                </div>
              </SectionContainer>
            </FlexRow>
          </OptionsWrapper>
        </FormBody>

        <ButtonWrapper>
          {createAdminAPIError && <div>{createAdminAPIError}</div>}
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isAdminCreating && <ClipLoader size={15} color="#fff" />}
              {!isAdminCreating && t("SUBMIT")}
            </>
          </Button>

          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CLOSE")}
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};

AddEditAdmin.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  title: PropTypes.string.isRequired,
  isEditMode: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  updateRefeshStatus: PropTypes.func.isRequired,
};

AddEditAdmin.defaultProps = {
  id: null,
  isEditMode: false,
};
export default withTranslation()(AddEditAdmin);
